import { toast } from '@core/component/Toast/Toast';
import { emailClient } from '@service-email/client';
import { Button, Dialog } from '@ui';
import { type Component, createSignal, Show } from 'solid-js';

export type ImapConnectModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a mailbox is successfully connected (e.g. to refresh links). */
  onConnected?: (linkId: string) => void;
};

/**
 * Modal form for connecting a non-Gmail mailbox over IMAP.
 *
 * Gmail uses OAuth (a popup/redirect); IMAP has no OAuth, so this collects the
 * server + credentials directly and POSTs them to `/email/links/imap`, which
 * validates them with a live IMAP login before storing the password encrypted.
 */
export const ImapConnectModal: Component<ImapConnectModalProps> = (props) => {
  const [email, setEmail] = createSignal('');
  const [imapHost, setImapHost] = createSignal('');
  const [imapPort, setImapPort] = createSignal('993');
  const [smtpHost, setSmtpHost] = createSignal('');
  const [smtpPort, setSmtpPort] = createSignal('587');
  const [username, setUsername] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [pending, setPending] = createSignal(false);

  // Convenience: default the SMTP host + username to match the email/IMAP host
  // so the common case (same server, address-as-username) needs minimal typing.
  const onEmailBlur = () => {
    if (!username()) setUsername(email());
    const domain = email().split('@')[1];
    if (domain && !imapHost()) setImapHost(`mail.${domain}`);
    if (domain && !smtpHost()) setSmtpHost(`mail.${domain}`);
  };

  const canSubmit = () =>
    !pending() &&
    email().includes('@') &&
    imapHost().length > 0 &&
    smtpHost().length > 0 &&
    username().length > 0 &&
    password().length > 0;

  const reset = () => {
    setEmail('');
    setImapHost('');
    setImapPort('993');
    setSmtpHost('');
    setSmtpPort('587');
    setUsername('');
    setPassword('');
  };

  const onSubmit = async () => {
    if (!canSubmit()) return;
    setPending(true);
    const result = await emailClient.connectImap({
      email_address: email().trim(),
      imap_host: imapHost().trim(),
      imap_port: Number(imapPort()) || 993,
      smtp_host: smtpHost().trim(),
      smtp_port: Number(smtpPort()) || 587,
      username: username().trim(),
      password: password(),
    });
    setPending(false);
    result.match(
      (res) => {
        toast.success('Mailbox connected — syncing will begin shortly.');
        reset();
        props.onConnected?.(res.link_id);
        props.onOpenChange(false);
      },
      () => {
        toast.failure(
          'Could not connect. Check the server, username, and password.'
        );
      }
    );
  };

  const field = (
    label: string,
    value: () => string,
    setValue: (v: string) => void,
    opts?: { type?: string; placeholder?: string; onBlur?: () => void }
  ) => (
    <label class="flex flex-col gap-1">
      <span class="text-xs text-muted">{label}</span>
      <input
        class="rounded border border-edge-muted bg-surface px-2 py-1.5 text-sm outline-none focus:border-edge"
        type={opts?.type ?? 'text'}
        value={value()}
        placeholder={opts?.placeholder}
        disabled={pending()}
        onInput={(e) => setValue(e.currentTarget.value)}
        onBlur={() => opts?.onBlur?.()}
      />
    </label>
  );

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      position="center"
      class="bg-surface rounded-lg border border-edge-muted"
    >
      <div class="flex flex-col gap-4 p-5">
        <Dialog.Title class="text-base font-semibold">
          Connect a mailbox (IMAP)
        </Dialog.Title>
        <p class="text-xs text-muted">
          For non-Gmail accounts. Macro connects to your mail server directly;
          your password is stored encrypted and only used to sync mail.
        </p>

        <div class="grid grid-cols-2 gap-3">
          <div class="col-span-2">
            {field('Email address', email, setEmail, {
              type: 'email',
              placeholder: 'you@example.com',
              onBlur: onEmailBlur,
            })}
          </div>
          {field('IMAP server', imapHost, setImapHost, {
            placeholder: 'mail.example.com',
          })}
          {field('IMAP port', imapPort, setImapPort, { placeholder: '993' })}
          {field('SMTP server', smtpHost, setSmtpHost, {
            placeholder: 'mail.example.com',
          })}
          {field('SMTP port', smtpPort, setSmtpPort, { placeholder: '587' })}
          {field('Username', username, setUsername, {
            placeholder: 'usually your email address',
          })}
          {field('Password', password, setPassword, { type: 'password' })}
        </div>

        <div class="flex flex-row justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            depth={3}
            disabled={pending()}
            onClick={() => props.onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="base"
            size="sm"
            depth={3}
            disabled={!canSubmit()}
            onClick={onSubmit}
          >
            <Show when={pending()} fallback="Connect">
              Connecting…
            </Show>
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
